/**
 * Device Detection Utilities
 * Detects device type (desktop/mobile) for responsive features
 */

import { useState, useEffect } from 'react';

export interface DeviceInfo {
  isMobile: boolean;
  isDesktop: boolean;
  isTablet: boolean;
  deviceType: 'mobile' | 'desktop' | 'tablet';
  screenWidth: number;
  screenHeight: number;
  userAgent: string;
}

/**
 * Detect if the current device is mobile
 */
export function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false;
  
  // Check user agent for mobile patterns
  const userAgent = navigator.userAgent || navigator.vendor || (window as unknown as { opera?: string }).opera;
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  
  // Check screen width (mobile-first approach)
  const isMobileWidth = window.innerWidth <= 768;
  
  // Check touch capability
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  return mobileRegex.test(userAgent) || (isMobileWidth && isTouchDevice);
}

/**
 * Detect if the current device is desktop
 */
export function isDesktopDevice(): boolean {
  if (typeof window === 'undefined') return true; // Default to desktop for SSR
  
  return !isMobileDevice() && !isTabletDevice();
}

/**
 * Detect if the current device is tablet
 */
export function isTabletDevice(): boolean {
  if (typeof window === 'undefined') return false;
  
  const userAgent = navigator.userAgent || navigator.vendor || (window as unknown as { opera?: string }).opera;
  const tabletRegex = /iPad|Android(?=.*\bMobile\b)(?=.*\bSafari\b)|Android(?=.*(?:\bTablet\b|\bTab\b))/i;
  
  // Check for tablet-specific screen sizes
  const isTabletWidth = window.innerWidth >= 768 && window.innerWidth <= 1024;
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  return tabletRegex.test(userAgent) || (isTabletWidth && isTouchDevice);
}

/**
 * Get comprehensive device information
 */
export function getDeviceInfo(): DeviceInfo {
  const isMobile = isMobileDevice();
  const isTablet = isTabletDevice();
  const isDesktop = isDesktopDevice();
  
  let deviceType: 'mobile' | 'desktop' | 'tablet' = 'desktop';
  if (isMobile) deviceType = 'mobile';
  else if (isTablet) deviceType = 'tablet';
  
  return {
    isMobile,
    isDesktop,
    isTablet,
    deviceType,
    screenWidth: typeof window !== 'undefined' ? window.innerWidth : 1920,
    screenHeight: typeof window !== 'undefined' ? window.innerHeight : 1080,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : ''
  };
}

/**
 * Hook to get device information with reactive updates
 */
export function useDeviceDetection() {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(() => getDeviceInfo());
  
  useEffect(() => {
    const updateDeviceInfo = () => {
      setDeviceInfo(getDeviceInfo());
    };
    
    // Update on resize
    window.addEventListener('resize', updateDeviceInfo);
    window.addEventListener('orientationchange', updateDeviceInfo);
    
    // Initial update
    updateDeviceInfo();
    
    return () => {
      window.removeEventListener('resize', updateDeviceInfo);
      window.removeEventListener('orientationchange', updateDeviceInfo);
    };
  }, []);
  
  return deviceInfo;
}